import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ford F-150 (classic OBS "old body style", ~1996 regular cab) — a big, boxy
// American full-size pickup at true scale (1 unit = 1 metre): length ~5.28,
// width 1.88 over the body (about 2.05 over the tyres), height 1.79 (tall),
// wheelbase 3.20, on chunky 0.39-radius tyres with a high ride. Nose is +Z.
// Signature cues: full-size boxy proportions; a short upright CAB over the front
// axle (upright windscreen, flat roof, big door glass, a rear cab window) that
// stands clearly TALLER than the bed; a long flat hood; a big rectangular front
// grille with a chrome surround and horizontal bars, a chrome Ford-oval hint,
// rectangular headlamps flanking it, amber corner lamps, a chrome bumper over a
// dark valance with round fog lamps; big tow mirrors; and behind the cab an OPEN
// CARGO BED — flat body-colour bedsides with a black top rail, a recessed dark
// bed floor, rear wheel arches in the bedsides, and a drop TAILGATE with a chrome
// handle at the rear, flanked by vertical tail lamps. Body panels carry the
// "paint" recolour slot (iconic Ford red); `drive` spins the wheels and
// `hazards` blinks the corner/side/rear amber indicators.

const PAINT = "#c0392b"; // iconic Ford red — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, rails, mud flaps)
const CLAD = "#26282a"; // matte-dark valances / aprons
const WELL = "#141518"; // wheel wells and arch brows
const LINER = "#33363b"; // dark bed floor / interior liner (reads recessed)
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6"; // bumpers, grille surround, trim strips
const STEEL = "#c4c9cf"; // bright wheel dish
const HUB = "#9aa0a0"; // hub / lug detail
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp / fog glass, reverse lamps
const AMBER = "#e8a13a";
const RED = "#a91527";
const REDDARK = "#701020";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.39; // chunky full-size truck tyre
const WHEEL_X = 0.86; // half track (tyre outer faces proud of the flares)
const AXLE_F = 1.6;
const AXLE_R = -1.6; // wheelbase 3.20
const BODY_HW = 0.94; // slab body half width (1.88 over the panels)
const TAIL = -2.5; // tailgate outer face; step bumper stands proud
const BED_FLOOR = 0.82; // top of the recessed bed floor (clears the rear tyre)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One chunky truck wheel: wide tyre, brake disc, a bright dish recessed within
 *  the tyre's outer face, a ring of lug bolts and a chrome centre cap. Every
 *  part shares `name` so the `drive` track spins them as one node about the
 *  axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.34, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.03, 16, BRAKE, [x - side * 0.1, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.29, 0.29, 0.15, 18, STEEL, [x + side * 0.09, y, z], [0, 0, Math.PI / 2], { name }), // bright dish, near-flush with the tyre face
    cyl(0.11, 0.11, 0.05, 14, HUB, [x + side * 0.17, y, z], [0, 0, Math.PI / 2], { name }), // hub boss
  ];
  for (let k = 0; k < 6; k++) {
    const a = (k * 2 * Math.PI) / 6;
    parts.push(
      cyl(0.026, 0.026, 0.05, 8, HUB, [x + side * 0.18, y + 0.16 * Math.cos(a), z + 0.16 * Math.sin(a)], [0, 0, Math.PI / 2], { name }),
    );
  }
  parts.push(cyl(0.055, 0.055, 0.05, 12, CHROME, [x + side * 0.19, y, z], [0, 0, Math.PI / 2], { name })); // centre cap
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-dark brow — an upper half-torus over the tyre. The [0, pi/2, 0]
 *  yaw puts the torus in the YZ plane with its 0..pi arc sweeping exactly the
 *  top half. The tyre outer face (0.86 + 0.17 = 1.03) is proud of both. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.5, 0.5, 0.04, 18, WELL, [side * (BODY_HW + 0.01), WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.47, 0.06, 6, 18, Math.PI),
      color: WELL,
      position: [side * (BODY_HW + 0.03), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
    },
  ];
}

// The six hazard indicators: amber corner lamps beside the headlamps, amber side
// markers on the front fenders, and amber lower segments of the rear tail lamps.
// Parts and blink tracks are generated from one table so the pivots (each part's
// own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.86, 0.98, 2.48], size: [0.12, 0.16, 0.05] },
  { name: "indicator-fr", pos: [0.86, 0.98, 2.48], size: [0.12, 0.16, 0.05] },
  { name: "indicator-ml", pos: [-0.955, 0.95, 2.15], size: [0.02, 0.06, 0.12] },
  { name: "indicator-mr", pos: [0.955, 0.95, 2.15], size: [0.02, 0.06, 0.12] },
  { name: "indicator-rl", pos: [-0.82, 0.78, -2.53], size: [0.12, 0.12, 0.06] },
  { name: "indicator-rr", pos: [0.82, 0.78, -2.53], size: [0.12, 0.12, 0.06] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.06, 0.06, 1.6, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // solid axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.18, 4.6, TRIM, [0, 0.46, -0.05])); // chassis / underbody rail (high clearance)
  P.push(box(1.5, 0.2, 0.55, CLAD, [0, 0.46, 2.15])); // front apron, fills under the nose back to the wells
  P.push(box(1.4, 0.2, 0.55, CLAD, [0, 0.46, -2.2])); // rear apron, fills under the tail back to the wells

  // -- Front + cab lower body (paint): one slab tub, sill to beltline ---------
  P.push(paint(2 * BODY_HW, 0.65, 2.25, [0, 0.875, 1.325])); // front fenders + doors + cowl (z 0.2..2.45)
  // Long flat hood, standing on the front clip and dipping toward the grille.
  P.push(paint(1.7, 0.1, 0.82, [0, 1.16, 2.02], [0.06, 0, 0]));
  P.push(box(1.5, 0.05, 0.12, TRIM, [0, 1.18, 1.55])); // cowl vent at the windscreen base

  // -- Bed: an OPEN cargo tub behind the cab ---------------------------------
  // Solid lower bed (paint) from the sill up to the bed floor; the rear wheels
  // arch into its sides. Above it the bed is OPEN.
  P.push(paint(2 * BODY_HW, 0.27, 2.75, [0, 0.685, -1.15])); // lower bedside block (z -2.525..0.225)
  P.push(box(1.7, 0.05, 2.6, LINER, [0, BED_FLOOR, -1.15])); // recessed dark bed floor
  for (let i = -2; i <= 2; i++) P.push(box(0.03, 0.03, 2.55, "#3d4046", [i * 0.34, BED_FLOOR + 0.03, -1.15])); // floor ribs
  for (const s of S) {
    P.push(paint(0.1, 0.42, 2.72, [s * 0.9, 1.01, -1.16])); // exterior bedside (floor 0.82..rail 1.24)
    P.push(box(0.03, 0.42, 2.55, LINER, [s * 0.82, 1.01, -1.15])); // inner bed wall, darker (reads recessed)
    P.push(box(0.14, 0.05, 2.72, TRIM, [s * 0.9, 1.26, -1.16])); // black top rail cap along the bedside
    for (const zp of [-0.5, -1.7]) P.push(box(0.12, 0.06, 0.12, "#1f2124", [s * 0.9, 1.28, zp])); // stake-pocket caps on the rail
  }
  P.push(paint(1.82, 0.44, 0.09, [0, 1.02, 0.18])); // bed headboard (front wall, behind the cab)
  P.push(box(1.62, 0.42, 0.03, LINER, [0, 1.02, 0.13])); // headboard inner face
  P.push(box(1.82, 0.05, 0.14, TRIM, [0, 1.26, 0.18])); // headboard top rail cap

  // -- Cab greenhouse (clearly taller than the bed rails) --------------------
  P.push(box(1.5, 0.52, 0.05, SCREEN, [0, 1.47, 1.46], [-0.22, 0, 0])); // upright windscreen
  for (const s of S) P.push(paint(0.12, 0.54, 0.09, [s * 0.72, 1.47, 1.46], [-0.22, 0, 0])); // A-pillars
  for (const s of S) P.push(box(0.34, 0.014, 0.03, TRIM, [s * 0.34, 1.24, 1.5], [-0.22, 0, 0])); // wipers
  P.push(paint(1.56, 0.09, 1.22, [0, 1.75, 0.86])); // flat cab roof, front edge caps the windscreen
  P.push(box(1.5, 0.1, 0.06, PAINT, [0, 1.24, 0.28], undefined, { slot: "paint" })); // cab back header (closes below the rear glass)
  P.push(box(1.32, 0.36, 0.05, GLASS, [0, 1.44, 0.29], [0.2, 0, 0])); // rear cab window
  for (const s of S) {
    P.push(box(0.03, 0.4, 0.86, GLASS, [s * 0.9, 1.42, 0.92], [0, 0, s * 0.05])); // door side glass
    P.push(paint(0.06, 0.42, 0.09, [s * 0.9, 1.42, 0.5])); // B-pillar (rear cab post)
    P.push(paint(0.1, 0.4, 0.1, [s * 0.7, 1.44, 0.28])); // rear roof pillar beside the back glass
    P.push(box(0.02, 0.03, 0.86, CHROME, [s * 0.945, 1.21, 0.92])); // belt-line drip strip
  }

  // -- Front fascia: big rectangular grille, chrome bumper, fog lamps ---------
  P.push(box(1.86, 0.5, 0.06, TRIM, [0, 0.95, 2.42])); // dark fascia backing (no floating lamps)
  P.push(box(1.04, 0.44, 0.06, CHROME, [0, 0.97, 2.46])); // chrome grille surround
  P.push(box(0.9, 0.36, 0.04, "#2a2c2f", [0, 0.97, 2.48])); // dark grille aperture
  for (let j = 0; j < 5; j++) P.push(box(0.86, 0.03, 0.03, CHROME, [0, 0.79 + j * 0.09, 2.5])); // horizontal chrome bars
  P.push(box(0.24, 0.15, 0.02, TRIM, [0, 0.97, 2.5])); // dark oval-badge backing (frames the chrome oval)
  P.push(cyl(0.085, 0.085, 0.04, 16, CHROME, [0, 0.97, 2.52], [Math.PI / 2, 0, 0])); // Ford-oval badge hint (chrome disc)
  for (const s of S) {
    P.push(box(0.36, 0.24, 0.05, TRIM, [s * 0.68, 0.98, 2.45])); // headlamp surround
    P.push(box(0.32, 0.2, 0.06, LAMP, [s * 0.68, 0.98, 2.48])); // rectangular headlamp
    P.push(box(0.28, 0.06, 0.02, CHROME, [s * 0.68, 0.95, 2.51])); // headlamp inner bar
    P.push(cyl(0.06, 0.06, 0.05, 12, LAMP, [s * 0.5, 0.4, 2.56], [Math.PI / 2, 0, 0])); // round fog lamp
  }
  P.push(box(1.92, 0.2, 0.16, CHROME, [0, 0.5, 2.53])); // chrome front bumper (proud, wider than the grille)
  P.push(box(1.5, 0.18, 0.1, CLAD, [0, 0.34, 2.5])); // lower valance under the bumper
  P.push(box(0.46, 0.14, 0.02, TRIM, [0, 0.5, 2.615])); // front plate recess
  P.push(box(0.4, 0.11, 0.02, PLATE, [0, 0.5, 2.625])); // front number plate

  // -- Rear fascia: tailgate with a handle, vertical tail lamps, step bumper --
  P.push(paint(1.82, 0.44, 0.09, [0, 1.02, TAIL])); // drop tailgate
  P.push(box(1.6, 0.03, 0.02, CHROME, [0, 1.14, TAIL - 0.06])); // tailgate trim strip
  P.push(box(0.36, 0.06, 0.05, CHROME, [0, 1.0, TAIL - 0.06])); // tailgate handle
  for (const s of S) {
    P.push(box(0.13, 0.34, 0.06, RED, [s * 0.82, 0.98, -2.52])); // vertical tail lamp
    P.push(box(0.11, 0.28, 0.03, REDDARK, [s * 0.82, 0.99, -2.5])); // inner lens fill
    P.push(box(0.13, 0.09, 0.06, LAMP, [s * 0.82, 1.14, -2.53])); // reverse lamp segment (top)
  }
  P.push(box(1.7, 0.22, 0.18, CHROME, [0, 0.5, -2.58])); // chrome rear step bumper
  P.push(box(0.5, 0.1, 0.12, TRIM, [0, 0.44, -2.6])); // step notch in the bumper centre
  P.push(box(0.46, 0.14, 0.02, TRIM, [0, 0.72, -2.55])); // rear plate recess on the tailgate
  P.push(box(0.4, 0.11, 0.02, PLATE, [0, 0.72, -2.56])); // rear number plate

  // -- Side furniture: running boards, handles, big tow mirrors, mud flaps ----
  for (const s of S) {
    P.push(box(0.1, 0.06, 1.5, TRIM, [s * 0.95, 0.5, 0.85])); // running board / cab step
    P.push(box(0.02, 0.03, 0.14, CHROME, [s * 0.945, 1.04, 0.92])); // door handle
    P.push(box(0.02, 0.03, 0.95, CHROME, [s * 0.955, 0.86, 0.9])); // chrome body-side molding along the door
    // Big west-coast tow mirror on a stalk off the door front / A-pillar base.
    P.push(box(0.13, 0.03, 0.04, TRIM, [s * 0.95, 1.2, 1.35])); // mirror stalk
    P.push(paint(0.09, 0.3, 0.14, [s * 1.02, 1.2, 1.33])); // tall tow-mirror head
    P.push(box(0.02, 0.26, 0.11, CHROME, [s * 1.07, 1.2, 1.29])); // mirror glass
    P.push(box(0.11, 0.2, 0.03, TRIM, [s * 0.92, 0.3, -2.15])); // mud flap behind the rear wheel
  }

  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the truck toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const fordF150 = defineModel({
  id: "ford-f150",
  name: "Ford F-150",
  batchId: "cars",
  tags: ["car", "ford", "f150", "truck", "pickup", "classic"],
  summary: "A classic Ford F-150 OBS full-size pickup at true scale, in iconic Ford red.",
  description:
    "A classic Ford F-150 (OBS 'old body style', ~1996 regular cab) full-size " +
    "pickup at real 1 unit = 1 metre scale (about 5.28 long, 1.88 wide over the " +
    "body, 1.79 tall, 3.20 wheelbase, chunky 0.39-radius tyres, high ride). All " +
    "the F-150 cues: big boxy proportions; a short upright cab (upright " +
    "windscreen, flat roof, big door glass, rear cab window) standing clearly " +
    "taller than the bed; a long flat hood; a big rectangular grille with a " +
    "chrome surround, horizontal chrome bars and a chrome Ford-oval hint, " +
    "rectangular headlamps and amber corner lamps flanking it, a chrome bumper " +
    "over a dark valance with round fog lamps; big tow mirrors and running " +
    "boards; and behind the cab an OPEN cargo bed with flat body-colour " +
    "bedsides, a black top rail, a recessed dark bed floor, rear wheel arches " +
    "in the bedsides, and a drop tailgate with a chrome handle flanked by " +
    "vertical tail lamps. Body panels carry the 'paint' recolour slot (iconic " +
    "Ford red); `drive` spins the four wheels and `hazards` blinks the corner, " +
    "side and rear amber indicators.",
  author: "Fable 5",
  footprint: 2.75,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
